import { gql } from 'apollo-angular';
import Project from 'src/app/interfaces/project';
import Disposition from 'src/app/interfaces/disposition';
import DispositionType from 'src/app/interfaces/disposition-type';
import DispositionBov from 'src/app/interfaces/disposition-bov';
import DispositionLegalStatus from 'src/app/interfaces/disposition-legal-status';
import DispositionYieldRequestStatus from 'src/app/interfaces/disposition-yield-request-status';
import Status from 'src/app/interfaces/status';
import DispositionStatus from 'src/app/interfaces/disposition-status';
import DispositionDate from 'src/app/interfaces/disposition-date';
import DateType from 'src/app/interfaces/date-type';
import DateClass from 'src/app/interfaces/date-class';
import DispositionPipelineDate from 'src/app/interfaces/disposition-pipeline-date';
import DispositionProceedType from 'src/app/interfaces/disposition_proceed_type';
import DispositionDistribution from 'src/app/interfaces/disposition-distribution';
import DispositionDistributionProceed from 'src/app/interfaces/disposition-distribution-proceed';
import { User } from 'src/app/interfaces/user';

export type ProjectDispositionResponse = {
  project: Project;
  getDispositions: Disposition[];
  dispositionTypes: DispositionType[];
  dispositionBovs: DispositionBov[];
  dispositionLegalStatuses: DispositionLegalStatus[];
  dispositionYieldRequestStatuses: DispositionYieldRequestStatus[];
  dispositionProceedTypes: DispositionProceedType[];
  statusesBySequence: Status[];
  dateTypes: DateType[];
  date_class_by_name: DateClass | null;
  me: User;
};
export type ProjectDispositionVariables = {
  id: string;
};
export type CreateDispositionResponse = {
  createDisposition: Disposition;
};
export type CreateDispositionVariables = {
  project_id: string;
};
export type UpdateDispositionResponse = {
  updateDisposition: Disposition;
  upsertDispositionStatus: DispositionStatus | null;
  upsertDispositionDates: DispositionDate[];
};
export type UpsertDispositionStatus = {
  id?: string;
  status_id: string | null;
  status_sequence_id: number;
  disposition_id: string;
};
export type DispositionDateInput = {
  id?: string;
  disposition_id: string;
  date_type_id: string;
  date_class_id: string;
  date: string;
};
export type DispositionInput = {
  organization_id: string | null;
  disposition_type_id: number | null;
  disposition_bov_id: number | null;
  purchaser: string | null;
  related_party: boolean | null;
  board_consent_na: boolean | null;
  investor_consent_na: boolean | null;
  crea_rofr: boolean | null;
  recapture_bond: boolean | null;
  recapture_exposure: number | null;
  disposition_legal_status_id: number | null;
  disposition_yield_request_status_id: number | null;
  monitoring_compliance: boolean | null;
  total_distribution_amount: number | null;
  is_final: boolean | null;
};

export type UpdateDispositionVariables = {
  id: string;
  input: DispositionInput;
  dispositionStatusInput: UpsertDispositionStatus | null;
  dispositionPipelineDates: DispositionPipelineDate[];
  dispositionDatesInput: DispositionDateInput[];
};
export type CreateDispositionDistributionResponse = {
  createDispositionDistribution: DispositionDistribution;
};
export type CreateDispositionDistributionVariables = {
  dispositionId: string;
};
export type DispositionDistributionProceedInput = {
  disposition_distribution_id: string;
  fund_id: string | null;
  organization_id: string | null;
  recipient: string | null;
};
export type CreateDispositionDistributionProceedVariables = {
  dispositionDistributionProceedInput: DispositionDistributionProceedInput;
};
export type CreateDispositionDistributionProceedResponse = {
  createDispositionDistributionProceed: DispositionDistributionProceed;
};
export type UpdateDispositionDistributionProceedInput = {
  id: string;
  disposition_proceed_type_id: number | null;
  amount: number;
};
export type UpdateDispositionDistributionProceedHasMany = {
  update: UpdateDispositionDistributionProceedInput[];
};
export type UpdateDispositionDistributionInput = {
  distribution_date: string | null;
  total: number;
  dispositionDistributionProceeds: UpdateDispositionDistributionProceedHasMany;
};
export type UpdateDispositionDistributionVariables = {
  id: string;
  input: UpdateDispositionDistributionInput;
};
export type UpdateDispositionDistributionResponse = {
  updateDispositionDistribution: DispositionDistribution;
};
const DISPOSITION_DISTRIBUTION_PROCEED_FRAGMENT = gql`
  fragment DispositionDistributionProceedFields on DispositionDistributionProceed {
    id
    disposition_distribution_id
    fund_id
    organization_id
    amount
    disposition_proceed_type_id
    fund {
      id
      name
    }
    organization {
      id
      name
    }
    recipient
  }
`;
export const dispositionFields = gql`
  fragment DispositionFields on Disposition {
    id
    organization {
      id
      name
    }
    purchaser
    disposition_type_id
    disposition_bov_id
    related_party
    recapture_bond
    recapture_exposure
    is_final
    disposition_legal_status_id
    disposition_yield_request_status_id
    crea_rofr
    monitoring_compliance
    board_consent_na
    investor_consent_na
    permission {
      _create
      _edit
      _read
      _delete
    }
    dispositionStatuses {
      id
      disposition_id
      status_id
      status_sequence_id
    }
    dispositionPipelineDates {
      id
      disposition_id
      pipeline_milestone_id
      date
    }
    dispositionDates {
      id
      disposition_id
      date_type_id
      date_class_id
      date
    }
    dispositionDistributions {
      id
      total
      distribution_date
      dispositionDistributionProceeds {
        ...DispositionDistributionProceedFields
      }
    }
  }
  ${DISPOSITION_DISTRIBUTION_PROCEED_FRAGMENT}
`;
const UPDATE_DISPOSITION = gql<
  UpdateDispositionResponse,
  UpdateDispositionVariables
>`
  #graphql
  mutation updateDisposition(
    $id: ID!
    $input: DispositionInput!
    $dispositionStatusInput: UpsertDispositionStatus
    $dispositionDatesInput: [DispositionDateInput!]!
    $dispositionPipelineDates: [DispositionPipelineDateInput!]!
  ) {
    upsertDispositionDates(input: $dispositionDatesInput) {
      id
      date_type_id
      date_class_id
      disposition_id
      date
    }
    upsertDispositionStatus(input: $dispositionStatusInput) {
      id
      status_id
      status_sequence_id
      disposition_id
    }
    upsertDispositionDates(input: $dispositionDatesInput) {
      id
      date
      disposition_id
      date_type_id
      date_class_id
    }
    upsertDispositionPipelineDates(input: $dispositionPipelineDates) {
      id
      disposition_id
      pipeline_milestone_id
      date
    }
    updateDisposition(id: $id, input: $input) {
      ...DispositionFields
    }
  }
  ${dispositionFields}
`;
const CREATE_DISPOSITION = gql<
  CreateDispositionResponse,
  CreateDispositionVariables
>`
  #graphql
  mutation createDisposition($project_id: ID!) {
    createDisposition(project_id: $project_id) {
      ...DispositionFields
    }
  }
  ${dispositionFields}
`;

const FETCH_PROJECT_DISPOSITIONS = gql<
  ProjectDispositionResponse,
  ProjectDispositionVariables
>`
  #graphql
  query getProjectDispositions($id: ID!) {
    project(id: $id) {
      id
      name
      projectCredits {
        crea_syndicated
        equity_amount
        taxCreditType {
          id
          name
          level
        }
        taxCreditDesignation {
          id
          name
        }
        projectCreditOutlets {
          ownership
          fundProjectState {
            id
            name
          }
          fund {
            id
            name
            close_date
            fundType {
              id
              name
            }
            fundStatuses {
              id
              status {
                id
                name
              }
            }
          }
          certificatedOutlet {
            id
            organization {
              id
              name
            }
          }
        }
      }
      participants(
        withRole: [
          "Originator"
          "Disposition Associate",
          "Sponsor"
          "Co-Sponsor"
          "General Partner"
          "Asset Manager"
          "Developer"
          "Dispositions Legal Firm"
          "Dispositions Legal Contact"
        ]
      ) {
        id
        person {
          id
          name
        }
        organization {
          id
          name
        }
        role {
          name
        }
        sponsor {
          id
          name
          is_repeat
          nickname
        }
      }
      projectDetail {
        risk_rating
      }
      properties {
        total_units
        entity {
          id
          entityAddresses {
            address {
              zipCode {
                city
                state {
                  abbreviation
                }
              }
            }
          }
        }
      }
    }
    getDispositions(project_id: $id) {
      ...DispositionFields
    }
    dispositionYieldRequestStatuses {
      id
      name
      index
    }
    dispositionProceedTypes {
      id
      name
    }
    dispositionLegalStatuses {
      id
      name
      index
    }
    dispositionBovs {
      id
      name
      index
    }
    dispositionTypes {
      id
      name
    }
    dateTypes(model: "dispositions") {
      id
      name
      model
    }
    date_class_by_name(name: "actual") {
      id
      name
    }
    me {
      permissionsForResource(resourceType: DISPOSITION) {
        _create
        _read
        _edit
        _delete
      }
    }
    statusesBySequence(sequence_name: "Disposition") {
      id
      name
      statusSequenceDetails(
        hasStatusSequence: { column: NAME, operator: EQ, value: "Disposition" }
      ) {
        id
        status_sequence_id
        index
        milestoneStatus {
          id
          pipeline_milestone_id
        }
      }
    }
  }
  ${dispositionFields}
  ${DISPOSITION_DISTRIBUTION_PROCEED_FRAGMENT}
`;

const CREATE_DISPOSITION_DISTRIBUTION = gql<
  CreateDispositionDistributionResponse,
  CreateDispositionDistributionVariables
>`
  mutation createDispositionDistribution($dispositionId: ID!) {
    createDispositionDistribution(disposition_id: $dispositionId) {
      id
      disposition_id
      total
      dispositionDistributionProceeds {
        ...DispositionDistributionProceedFields
      }
    }
  }
  ${DISPOSITION_DISTRIBUTION_PROCEED_FRAGMENT}
`;
const CREATE_DISPOSITION_DISTRIBUTION_PROCEED = gql<
  CreateDispositionDistributionProceedResponse,
  CreateDispositionDistributionProceedVariables
>`
  mutation createDispositionDistributionProceed(
    $dispositionDistributionProceedInput: CreateDispositionDistributionProceedInput
  ) {
    createDispositionDistributionProceed(
      input: $dispositionDistributionProceedInput
    ) {
      ...DispositionDistributionProceedFields
    }
  }
  ${DISPOSITION_DISTRIBUTION_PROCEED_FRAGMENT}
`;
const UPDATE_DISPOSITION_DISTRIBUTION = gql<
  UpdateDispositionDistributionResponse,
  UpdateDispositionDistributionVariables
>`
  mutation updateDispositionDistribution(
    $id: ID!
    $input: UpdateDispositionDistributionInput!
  ) {
    updateDispositionDistribution(id: $id, input: $input) {
      id
      disposition_id
      distribution_date
      total
      dispositionDistributionProceeds {
        ...DispositionDistributionProceedFields
      }
    }
  }
  ${DISPOSITION_DISTRIBUTION_PROCEED_FRAGMENT}
`;
const DELETE_DISPOSITION = gql`
  mutation deleteDisposition($id: ID!) {
    deleteDisposition(id: $id) {
      id
    }
  }
`;
const DELETE_DISPOSITION_DISTRIBUTION = gql`
  mutation deleteDispositionDistribution($id: ID!) {
    deleteDispositionDistribution(id: $id) {
      id
    }
  }
`;
const DELETE_DISPOSITION_DISTRIBUTION_PROCEED = gql`
  mutation deleteDispositionDistributionProceed($id: ID!) {
    deleteDispositionDistributionProceed(id: $id) {
      id
    }
  }
`;
export {
  FETCH_PROJECT_DISPOSITIONS,
  CREATE_DISPOSITION,
  UPDATE_DISPOSITION,
  CREATE_DISPOSITION_DISTRIBUTION,
  CREATE_DISPOSITION_DISTRIBUTION_PROCEED,
  UPDATE_DISPOSITION_DISTRIBUTION,
  DELETE_DISPOSITION,
  DELETE_DISPOSITION_DISTRIBUTION,
  DELETE_DISPOSITION_DISTRIBUTION_PROCEED,
};
